import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Loader2,
  Users,
  ArrowLeft,
  ChevronDown,
} from 'lucide-react'
import { AppShell } from '@/components/shell'
import { useAuth } from '@/context/AuthContext'
import { supabase } from '@/integrations/supabase/client'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDeadline(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })
}

function timeUntil(dateStr: string): string {
  const diffMs = new Date(dateStr).getTime() - Date.now()
  if (diffMs <= 0) return 'Passed'
  const hrs = Math.floor(diffMs / 3600000)
  const days = Math.floor(hrs / 24)
  if (days > 0) return `${days}d ${hrs % 24}h`
  if (hrs > 0) return `${hrs}h`
  const mins = Math.floor(diffMs / 60000)
  return `${mins}m`
}

function relativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diffMs / 60000)
  const hrs = Math.floor(diffMs / 3600000)
  const days = Math.floor(diffMs / 86400000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  if (hrs < 24) return `${hrs}h ago`
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function emailTypeLabel(type: string, cadenceKey?: string | null): string {
  switch (type) {
    case 'matchday-open': return 'Picks Open'
    case 'matchday-reminder': return `Reminder${cadenceKey ? ` (${cadenceKey})` : ''}`
    case 'matchday-selections': return 'Selections'
    case 'matchday-results': return 'Results'
    default: return type
  }
}

function emailTypeBadgeColor(type: string): string {
  switch (type) {
    case 'matchday-open': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
    case 'matchday-reminder': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    case 'matchday-selections': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
    case 'matchday-results': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
    default: return 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
  }
}

function cadenceToMs(cadence: string): number {
  if (cadence.endsWith('d')) return parseInt(cadence) * 86400000
  if (cadence.endsWith('h')) return parseInt(cadence) * 3600000
  if (cadence.endsWith('m')) return parseInt(cadence) * 60000
  return 0
}

function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

interface EmailChecklistItem {
  label: string
  emailType: string
  cadenceKey: string | null
  scheduledAt: string | null
  sentAt: string | null
  recipientCount: number | null
  status: 'sent' | 'scheduled' | 'pending'
}

function buildEmailChecklist(
  matchday: number,
  deadline: string | null,
  reminderCadence: string[],
  emailLogs: { email_type: string; matchday: number; cadence_key: string | null; recipients_count: number; sent_at: string }[],
  isCompleted: boolean,
): EmailChecklistItem[] {
  const mdLogs = emailLogs.filter(l => l.matchday === matchday)
  const findLog = (type: string, cadence?: string | null) =>
    mdLogs.find(l => l.email_type === type && (cadence === undefined || l.cadence_key === cadence))

  const items: EmailChecklistItem[] = []
  const now = Date.now()

  // Picks Open
  const openLog = findLog('matchday-open')
  items.push({
    label: 'Picks Open',
    emailType: 'matchday-open',
    cadenceKey: null,
    scheduledAt: null,
    sentAt: openLog?.sent_at ?? null,
    recipientCount: openLog?.recipients_count ?? null,
    status: openLog ? 'sent' : 'pending',
  })

  // Reminders
  for (const cadence of reminderCadence) {
    const reminderLog = findLog('matchday-reminder', cadence)
    const scheduledAt = deadline ? new Date(new Date(deadline).getTime() - cadenceToMs(cadence)).toISOString() : null
    const isScheduled = scheduledAt && new Date(scheduledAt).getTime() > now

    items.push({
      label: `Reminder (${cadence})`,
      emailType: 'matchday-reminder',
      cadenceKey: cadence,
      scheduledAt,
      sentAt: reminderLog?.sent_at ?? null,
      recipientCount: reminderLog?.recipients_count ?? null,
      status: reminderLog ? 'sent' : isScheduled ? 'scheduled' : 'pending',
    })
  }

  // Selections
  const selLog = findLog('matchday-selections')
  items.push({
    label: 'Selections',
    emailType: 'matchday-selections',
    cadenceKey: null,
    scheduledAt: deadline ? new Date(new Date(deadline).getTime() + 60000).toISOString() : null,
    sentAt: selLog?.sent_at ?? null,
    recipientCount: selLog?.recipients_count ?? null,
    status: selLog ? 'sent' : 'pending',
  })

  // Results
  const resLog = findLog('matchday-results')
  items.push({
    label: 'Results',
    emailType: 'matchday-results',
    cadenceKey: null,
    scheduledAt: null,
    sentAt: resLog?.sent_at ?? null,
    recipientCount: resLog?.recipients_count ?? null,
    status: resLog ? 'sent' : isCompleted ? 'pending' : 'pending',
  })

  return items
}

type Status = 'ok' | 'warn' | 'error' | 'pending' | 'none'

function StatusIcon({ status, className = 'h-4 w-4' }: { status: Status; className?: string }) {
  switch (status) {
    case 'ok':
      return <CheckCircle2 className={`${className} text-emerald-500`} />
    case 'warn':
      return <AlertTriangle className={`${className} text-amber-500`} />
    case 'error':
      return <XCircle className={`${className} text-red-500`} />
    case 'pending':
      return <Clock className={`${className} text-zinc-400 dark:text-zinc-500`} />
    default:
      return <span className={`${className} inline-block rounded-full bg-zinc-200 dark:bg-zinc-700`} />
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MatchdayStatus {
  matchday: number
  fixtureCount: number
  completedCount: number
  liveCount: number
  excludedFixtures: { home_team: string; away_team: string; kickoff_time: string; home_score: number | null; away_score: number | null }[]
  deadline: string | null
  hasSynced: boolean
  hasResults: boolean
  hasScored: boolean
  hasEmailSent: boolean
  picksSubmitted: number
  totalMembers: number
  status: 'upcoming' | 'live' | 'completed' | 'processing'
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function AdminLeague() {
  const { isSuperAdmin } = useAuth()
  const { leagueId } = useParams<{ leagueId: string }>()

  // League info
  const { data: league, isLoading: isLoadingLeague } = useQuery({
    queryKey: ['admin-league-info', leagueId],
    queryFn: async () => {
      const { data } = await supabase
        .from('leagues')
        .select('id, name, season')
        .eq('id', leagueId!)
        .single()
      return data
    },
    enabled: isSuperAdmin && !!leagueId,
  })

  // League config for competition info
  const { data: leagueConfig } = useQuery({
    queryKey: ['admin-league-config', leagueId],
    queryFn: async () => {
      const { data } = await supabase
        .from('league_configs')
        .select('competition_name, api_football_league_id')
        .eq('league_id', leagueId!)
        .single()
      return data
    },
    enabled: isSuperAdmin && !!leagueId,
  })

  const season = league?.season
  const competitionId = leagueConfig?.api_football_league_id

  // Fixtures
  const { data: fixtures = [], isLoading: isLoadingFixtures } = useQuery({
    queryKey: ['admin-league-fixtures', season, competitionId],
    queryFn: async () => {
      const { data } = await supabase
        .from('fixtures')
        .select('matchday, status, kickoff_time, home_team, away_team, home_score, away_score')
        .eq('season', season!)
        .eq('competition_id', competitionId!)
        .order('matchday')
        .order('kickoff_time')
      return data ?? []
    },
    enabled: isSuperAdmin && !!season && !!competitionId,
    refetchInterval: 60000,
  })

  // Pipeline status
  const { data: pipelineRows = [] } = useQuery({
    queryKey: ['admin-league-pipeline', season, leagueId],
    queryFn: async () => {
      const { data } = await supabase
        .from('pipeline_status')
        .select('matchday, fixtures_synced_at, results_populated_at, selections_scored_at, results_email_sent_at, autopicks_processed_at, errors')
        .eq('season', season!)
        .eq('league_id', leagueId!)
        .order('matchday', { ascending: false })
      return data ?? []
    },
    enabled: isSuperAdmin && !!leagueId && !!season,
    refetchInterval: 60000,
  })

  // Selections
  const { data: selections = [] } = useQuery({
    queryKey: ['admin-league-selections', season, leagueId],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_selections')
        .select('matchday, user_id')
        .eq('season', season!)
        .eq('league_id', leagueId!)
      return data ?? []
    },
    enabled: isSuperAdmin && !!leagueId && !!season,
    refetchInterval: 60000,
  })

  // Email send log
  const { data: emailLogs = [] } = useQuery({
    queryKey: ['admin-league-email-logs', season, leagueId],
    queryFn: async () => {
      const { data } = await supabase
        .from('email_send_log')
        .select('email_type, matchday, season, cadence_key, recipients_count, sent_at')
        .eq('season', season!)
        .eq('league_id', leagueId!)
        .order('sent_at', { ascending: false })
        .limit(50)
      return data ?? []
    },
    enabled: isSuperAdmin && !!leagueId && !!season,
    refetchInterval: 60000,
  })

  // Autopick + forfeit counts by matchday (with timestamp of when autopicks ran)
  const { data: autopickCounts = [] } = useQuery({
    queryKey: ['admin-league-autopicks', season, leagueId],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_selections')
        .select('matchday, is_forfeit, autopicked_at')
        .eq('season', season!)
        .eq('league_id', leagueId!)
        .not('autopicked_at', 'is', null)
      const counts = new Map<number, { autopicks: number; forfeits: number; ranAt: string | null }>()
      for (const row of data ?? []) {
        const entry = counts.get(row.matchday) ?? { autopicks: 0, forfeits: 0, ranAt: null }
        if (row.is_forfeit) entry.forfeits++
        else entry.autopicks++
        // Track the earliest autopicked_at as when the process ran
        if (row.autopicked_at && (!entry.ranAt || row.autopicked_at < entry.ranAt)) {
          entry.ranAt = row.autopicked_at
        }
        counts.set(row.matchday, entry)
      }
      return Array.from(counts.entries()).map(([matchday, { autopicks, forfeits, ranAt }]) => ({ matchday, autopicks, forfeits, ranAt }))
    },
    enabled: isSuperAdmin && !!leagueId && !!season,
    refetchInterval: 60000,
  })

  // League email config (for reminder cadence)
  const { data: emailConfig } = useQuery({
    queryKey: ['admin-league-email-config', leagueId],
    queryFn: async () => {
      const { data } = await supabase
        .from('league_configs')
        .select('email_config')
        .eq('league_id', leagueId!)
        .single()
      return data?.email_config as { reminder_cadence?: string[] } | null
    },
    enabled: isSuperAdmin && !!leagueId,
  })

  // Member count
  const { data: memberCount = 0 } = useQuery({
    queryKey: ['admin-league-members', leagueId],
    queryFn: async () => {
      const { count } = await supabase
        .from('league_memberships')
        .select('id', { count: 'exact', head: true })
        .eq('league_id', leagueId!)
        .eq('status', 'active')
      return count ?? 0
    },
    enabled: isSuperAdmin && !!leagueId,
  })

  // Build pipeline lookup
  const pipelineByMd = useMemo(() => {
    const map = new Map<number, typeof pipelineRows[0]>()
    for (const row of pipelineRows) {
      map.set(row.matchday, row)
    }
    return map
  }, [pipelineRows])

  // Build matchday statuses
  const matchdayStatuses: MatchdayStatus[] = useMemo(() => {
    if (!fixtures.length) return []

    const now = new Date()
    const mdMap = new Map<number, typeof fixtures>()
    for (const f of fixtures) {
      const list = mdMap.get(f.matchday) || []
      list.push(f)
      mdMap.set(f.matchday, list)
    }

    const finishedStatuses = new Set(['FT', 'AET', 'PEN'])
    const liveStatuses = new Set(['1H', '2H', 'HT', 'ET', 'BT', 'P'])
    const unplayedStatuses = new Set(['NS', 'TBD'])

    const statuses: MatchdayStatus[] = []

    for (const [md, mdFixtures] of Array.from(mdMap.entries()).sort((a, b) => a[0] - b[0])) {
      const completedCount = mdFixtures.filter(f => finishedStatuses.has(f.status)).length
      const liveCount = mdFixtures.filter(f => liveStatuses.has(f.status)).length

      // Use earliest UNPLAYED fixture for deadline — matchdays with rescheduled
      // fixtures may have completed matches with much earlier kickoff times
      const unplayedFixtures = mdFixtures.filter(f => unplayedStatuses.has(f.status))
      const earliestUnplayedKickoff = unplayedFixtures
        .map(f => f.kickoff_time)
        .sort()[0]
      // Fall back to absolute earliest if all fixtures are played
      const earliestKickoff = earliestUnplayedKickoff
        || mdFixtures.map(f => f.kickoff_time).sort()[0]
      const deadline = earliestUnplayedKickoff || null
      // Fixtures that played before the main batch (rescheduled/excluded)
      const excludedFixtures = earliestUnplayedKickoff
        ? mdFixtures
            .filter(f => finishedStatuses.has(f.status) && f.kickoff_time < earliestUnplayedKickoff)
            .map(f => ({ home_team: f.home_team, away_team: f.away_team, kickoff_time: f.kickoff_time, home_score: f.home_score, away_score: f.away_score }))
        : []

      const mdSelections = selections.filter(s => s.matchday === md)
      const picksSubmitted = mdSelections.length

      const pipeline = pipelineByMd.get(md)

      const allComplete = completedCount === mdFixtures.length
      const isLive = liveCount > 0
      const isPast = deadline ? new Date(deadline) < now : false

      let status: MatchdayStatus['status'] = 'upcoming'
      if (allComplete) status = 'completed'
      else if (isLive || (isPast && !allComplete)) status = 'live'
      else if (isPast) status = 'processing'

      statuses.push({
        matchday: md,
        fixtureCount: mdFixtures.length,
        completedCount,
        liveCount,
        excludedFixtures,
        deadline,
        hasSynced: !!pipeline?.fixtures_synced_at,
        hasResults: !!pipeline?.results_populated_at,
        hasScored: !!pipeline?.selections_scored_at,
        hasEmailSent: !!pipeline?.results_email_sent_at,
        picksSubmitted,
        totalMembers: memberCount,
        status,
      })
    }

    return statuses
  }, [fixtures, selections, pipelineByMd, memberCount])

  // Split matchdays
  const activeMd = matchdayStatuses.find(
    m => m.status === 'live' || m.status === 'upcoming'
  )
  const completedMds = matchdayStatuses.filter(m => m.status === 'completed')
  const recentCompleted = completedMds.slice(-10).reverse()

  const [expandedMd, setExpandedMd] = useState<number | null>(null)

  const isLoading = isLoadingLeague || isLoadingFixtures

  if (!isSuperAdmin) {
    return (
      <AppShell>
        <div className="text-center py-16 text-zinc-500 dark:text-zinc-400">
          <p className="text-lg font-medium">Access Denied</p>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="max-w-lg mx-auto px-4 pb-28 pt-4 space-y-6">
        {/* Header */}
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 mb-2 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" />
            System Status
          </Link>
          <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {league?.name ?? 'Loading...'}
          </h1>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
            {leagueConfig?.competition_name ?? ''} · {season ?? ''}
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
          </div>
        ) : (
          <>
            {/* Active Matchday */}
            {activeMd && (
              <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Matchday {activeMd.matchday}
                  </h2>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    activeMd.status === 'live'
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                      : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                  }`}>
                    {activeMd.status === 'live' ? 'Live' : 'Upcoming'}
                  </span>
                </div>

                {activeMd.deadline && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-500 dark:text-zinc-400">Deadline</span>
                    <span className="text-zinc-700 dark:text-zinc-300 font-medium">
                      {formatDeadline(activeMd.deadline)}
                      <span className="text-zinc-400 dark:text-zinc-500 ml-1.5">
                        ({timeUntil(activeMd.deadline)})
                      </span>
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between text-xs">
                  <span className="text-zinc-500 dark:text-zinc-400">Picks submitted</span>
                  <span className="flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-zinc-400" />
                    <span className={`font-medium ${
                      activeMd.picksSubmitted === activeMd.totalMembers
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-zinc-700 dark:text-zinc-300'
                    }`}>
                      {activeMd.picksSubmitted}/{activeMd.totalMembers}
                    </span>
                  </span>
                </div>

                {activeMd.status === 'live' && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-500 dark:text-zinc-400">Matches</span>
                    <span className="text-zinc-700 dark:text-zinc-300 font-medium">
                      {activeMd.completedCount}/{activeMd.fixtureCount} complete
                      {activeMd.liveCount > 0 && (
                        <span className="text-emerald-600 dark:text-emerald-400 ml-1">
                          ({activeMd.liveCount} live)
                        </span>
                      )}
                    </span>
                  </div>
                )}

                {activeMd.excludedFixtures.length > 0 && (
                  <div className="text-xs space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-500 dark:text-zinc-400">Excluded fixtures</span>
                      <span className="text-amber-600 dark:text-amber-400 font-medium">
                        {activeMd.excludedFixtures.length} already played
                      </span>
                    </div>
                    {activeMd.excludedFixtures.map((f, i) => (
                      <div key={i} className="flex items-center justify-between pl-2 text-[11px] text-zinc-400 dark:text-zinc-500">
                        <span>{f.home_team} vs {f.away_team}</span>
                        <span>
                          {f.home_score !== null ? `${f.home_score}–${f.away_score}` : ''}{' '}
                          · {formatShortDate(f.kickoff_time)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Autopicks */}
                {(() => {
                  const counts = autopickCounts.find(a => a.matchday === activeMd.matchday)
                  const pipeline = pipelineByMd.get(activeMd.matchday)
                  const processedAt = pipeline?.autopicks_processed_at ?? null
                  const deadlinePassed = activeMd.deadline && new Date(activeMd.deadline).getTime() < Date.now()
                  const parts: string[] = []
                  if (counts?.autopicks) parts.push(`${counts.autopicks} autopick${counts.autopicks > 1 ? 's' : ''}`)
                  if (counts?.forfeits) parts.push(`${counts.forfeits} forfeit${counts.forfeits > 1 ? 's' : ''}`)
                  // Scheduled time = deadline + 30s (autopicks run 30s after deadline)
                  const scheduledAt = activeMd.deadline
                    ? new Date(new Date(activeMd.deadline).getTime() + 30000)
                    : null
                  return (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-zinc-500 dark:text-zinc-400">Autopicks</span>
                      <span className="text-zinc-700 dark:text-zinc-300 font-medium">
                        {processedAt
                          ? parts.length > 0
                            ? parts.join(', ')
                            : 'None needed'
                          : deadlinePassed
                            ? 'Waiting...'
                            : scheduledAt
                              ? `Scheduled ${formatShortDate(scheduledAt.toISOString())}`
                              : 'After deadline'}
                      </span>
                      {processedAt && (
                        <span className="text-zinc-400 dark:text-zinc-500 text-[11px] font-normal ml-1.5">
                          · {formatShortDate(processedAt)}
                        </span>
                      )}
                    </div>
                  )
                })()}

                {/* Email Checklist */}
                <div className="border-t border-zinc-100 dark:border-zinc-800 pt-3 mt-1 space-y-1.5">
                  <p className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                    Emails
                  </p>
                  {buildEmailChecklist(
                    activeMd.matchday,
                    activeMd.deadline,
                    emailConfig?.reminder_cadence ?? ['3d', '24h', '6h'],
                    emailLogs,
                    activeMd.status === 'completed',
                  ).map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      {item.status === 'sent' ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                      ) : item.status === 'scheduled' ? (
                        <Clock className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                      ) : (
                        <span className="h-3.5 w-3.5 rounded-full border border-zinc-200 dark:border-zinc-600 shrink-0" />
                      )}
                      <span className="text-zinc-700 dark:text-zinc-300 min-w-0">
                        {item.label}
                      </span>
                      <span className="flex-1" />
                      <span className="text-zinc-400 dark:text-zinc-500 text-[11px] shrink-0">
                        {item.status === 'sent' && item.sentAt
                          ? `${item.recipientCount} sent · ${formatShortDate(item.sentAt)}`
                          : item.status === 'scheduled' && item.scheduledAt
                            ? formatShortDate(item.scheduledAt)
                            : item.emailType === 'matchday-results'
                              ? 'After all matches'
                              : item.emailType === 'matchday-selections'
                                ? 'After deadline'
                                : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pipeline Status */}
            <div className="space-y-2">
              <h2 className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider px-1">
                Completed Matchdays
              </h2>
              <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 divide-y divide-zinc-100 dark:divide-zinc-800">
                {recentCompleted.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-zinc-400 dark:text-zinc-500">
                    No completed matchdays yet
                  </div>
                ) : (
                  recentCompleted.map((md) => {
                    const isExpanded = expandedMd === md.matchday
                    const counts = autopickCounts.find(a => a.matchday === md.matchday)
                    const autoParts: string[] = []
                    if (counts?.autopicks) autoParts.push(`${counts.autopicks} autopick${counts.autopicks > 1 ? 's' : ''}`)
                    if (counts?.forfeits) autoParts.push(`${counts.forfeits} forfeit${counts.forfeits > 1 ? 's' : ''}`)

                    return (
                      <div key={md.matchday}>
                        <button
                          onClick={() => setExpandedMd(isExpanded ? null : md.matchday)}
                          className="w-full px-4 py-3 flex items-center gap-4 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
                        >
                          <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300 w-12 shrink-0 text-left">
                            MD {md.matchday}
                          </span>
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex items-center gap-1" title="Fixtures synced">
                              <StatusIcon status={md.hasSynced ? 'ok' : 'pending'} className="h-3.5 w-3.5" />
                              <span className="text-[11px] text-zinc-500 dark:text-zinc-400">Synced</span>
                            </div>
                            <div className="flex items-center gap-1" title="Results scored">
                              <StatusIcon status={md.hasScored ? 'ok' : md.hasResults ? 'warn' : 'pending'} className="h-3.5 w-3.5" />
                              <span className="text-[11px] text-zinc-500 dark:text-zinc-400">Scored</span>
                            </div>
                            <div className="flex items-center gap-1" title="Results email sent">
                              <StatusIcon status={md.hasEmailSent ? 'ok' : 'pending'} className="h-3.5 w-3.5" />
                              <span className="text-[11px] text-zinc-500 dark:text-zinc-400">Email</span>
                            </div>
                          </div>
                          <span className="text-[11px] text-zinc-400 dark:text-zinc-500 shrink-0">
                            {md.picksSubmitted}/{md.totalMembers} picks
                          </span>
                          <ChevronDown className={`h-3.5 w-3.5 text-zinc-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>

                        {isExpanded && (
                          <div className="px-4 pb-3 space-y-2">
                            {/* Autopicks */}
                            <div className="flex items-center justify-between text-xs pl-12">
                              <span className="text-zinc-500 dark:text-zinc-400">Autopicks</span>
                              <span className="text-zinc-700 dark:text-zinc-300 font-medium">
                                {autoParts.length > 0 ? autoParts.join(', ') : 'None'}
                              </span>
                            </div>

                            {/* Email checklist */}
                            <div className="pl-12 space-y-1.5 pt-1">
                              <p className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                                Emails
                              </p>
                              {buildEmailChecklist(
                                md.matchday,
                                md.deadline,
                                emailConfig?.reminder_cadence ?? ['3d', '24h', '6h'],
                                emailLogs,
                                true,
                              ).map((item, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                  {item.status === 'sent' ? (
                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                                  ) : (
                                    <span className="h-3.5 w-3.5 rounded-full border border-zinc-200 dark:border-zinc-600 shrink-0" />
                                  )}
                                  <span className="text-zinc-700 dark:text-zinc-300">{item.label}</span>
                                  <span className="flex-1" />
                                  <span className="text-zinc-400 dark:text-zinc-500 text-[11px]">
                                    {item.status === 'sent' && item.sentAt
                                      ? `${item.recipientCount} sent · ${formatShortDate(item.sentAt)}`
                                      : '—'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
              </div>

              {completedMds.length > 10 && (
                <p className="text-center text-[11px] text-zinc-400 dark:text-zinc-500">
                  Showing last 10 of {completedMds.length} completed
                </p>
              )}
            </div>

            {/* Recent Emails */}
            <div className="space-y-2">
              <h2 className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider px-1">
                Recent Emails
              </h2>
              <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 divide-y divide-zinc-100 dark:divide-zinc-800">
                {emailLogs.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-zinc-400 dark:text-zinc-500">
                    No emails sent yet
                  </div>
                ) : (
                  emailLogs.map((log, i) => (
                    <div key={i} className="px-4 py-2.5 flex items-center gap-3">
                      <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 w-12 shrink-0">
                        MD {log.matchday}
                      </span>
                      <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${emailTypeBadgeColor(log.email_type)}`}>
                        {emailTypeLabel(log.email_type, log.cadence_key)}
                      </span>
                      <span className="flex-1" />
                      <span className="text-[11px] text-zinc-500 dark:text-zinc-400">
                        {log.recipients_count} sent
                      </span>
                      <span className="text-[11px] text-zinc-400 dark:text-zinc-500 w-16 text-right">
                        {relativeTime(log.sent_at)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

          </>
        )}
      </div>
    </AppShell>
  )
}
